import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { settingsApi } from '../services/api'

type Theme = 'light' | 'dark'

const STORAGE_KEY = 'theme'

function getInitialTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'light' || stored === 'dark') return stored
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

interface ThemeContextType {
  theme: Theme
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

/** Wraps the whole app (outside AuthProvider, in App.tsx) so the theme
 * applies on /login too, before any session exists. Initial value comes
 * from localStorage/prefers-color-scheme for an instant, flicker-free first
 * paint (see the matching inline script in index.html); once mounted, and
 * only if a session already exists, it reconciles with the server's
 * persisted dashboard_prefs.dark_mode so the choice follows the user
 * across devices. Skipped entirely with no session (see the effect below)
 * -- the local guess already gave a correct-looking page, and there's
 * nothing to sync yet. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(getInitialTheme)

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark')
    localStorage.setItem(STORAGE_KEY, theme)
  }, [theme])

  useEffect(() => {
    // Skip entirely when there's no session yet (e.g. on /login) -- calling
    // an authenticated endpoint here always 401s, and the app's global axios
    // interceptor treats any 401 as an expired session and force-reloads
    // into /login, which produced a visible reload loop on every
    // unauthenticated page view once this effect started firing on mount.
    if (!localStorage.getItem('auth_token')) return

    settingsApi
      .getDashboardPrefs()
      .then((res) => {
        const serverPref = res.data?.dashboard_prefs?.dark_mode
        if (typeof serverPref === 'boolean') {
          setTheme(serverPref ? 'dark' : 'light')
        }
      })
      .catch(() => {})
  }, [])

  const toggleTheme = () => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark'
    setTheme(next)
    settingsApi.updateDashboardPrefs({ dark_mode: next === 'dark' }).catch(() => {})
  }

  return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}
